/**
 * Pure helpers for the provider-neutral hosted checkout flow. Kept free of
 * browser globals (storage is injected) so they run in node-environment tests.
 */

export const PENDING_CHECKOUT_STORAGE_KEY = 'pendingCheckoutUuid';

/**
 * Validate and assemble the checkout request from component props and the
 * authenticated user.
 *
 * @param {Object|null} user - Auth user ({uuid, siteUuid, unitUuid})
 * @param {Object} options
 * @param {string|null} options.accountUuid - Account to credit
 * @param {string|null} options.unitUuid - Unit UUID; falls back to user.unitUuid
 * @param {number} options.amountCents - Integer cents, > 0
 * @returns {{siteUuid: string, accountUuid: string, unitUuid: string, userUuid: string, amountCents: number}}
 */
export const buildCheckoutParams = (user, { accountUuid, unitUuid, amountCents } = {}) => {
  const siteUuid = user?.siteUuid;
  const finalUnitUuid = unitUuid || user?.unitUuid;
  const userUuid = user?.uuid;

  if (!siteUuid) throw new Error('Could not determine the site for this payment');
  if (!accountUuid) throw new Error('Could not determine the account for this payment');
  if (!finalUnitUuid) throw new Error('Could not determine the unit for this payment');
  if (!userUuid) throw new Error('Could not determine the paying user');
  if (!Number.isInteger(amountCents) || amountCents <= 0) {
    throw new Error('Payment amount must be a positive whole number of cents');
  }

  return { siteUuid, accountUuid, unitUuid: finalUnitUuid, userUuid, amountCents };
};

/**
 * Stash the checkout uuid for the landing page. Storage failures (quota,
 * privacy mode) must not abort the checkout - the stash is a UX reference
 * only; the webhook-credited balance is the real confirmation.
 *
 * @param {string} checkoutUuid
 * @param {Storage} storage - Injectable for tests; defaults to sessionStorage
 * @returns {boolean} Whether the stash was written
 */
export const stashPendingCheckoutUuid = (checkoutUuid, storage = window.sessionStorage) => {
  try {
    storage.setItem(PENDING_CHECKOUT_STORAGE_KEY, checkoutUuid);
    return true;
  } catch {
    return false;
  }
};

/**
 * Read and clear the stashed checkout uuid left by PaymentButton before the
 * redirect. The provider returns the shopper on a bare path with no query
 * string, so this stash is the only client-side reference to the attempt.
 *
 * @param {Storage} storage - Injectable for tests; defaults to sessionStorage
 * @returns {string|null}
 */
export const popPendingCheckoutUuid = (storage = window.sessionStorage) => {
  try {
    const checkoutUuid = storage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
    storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
    return checkoutUuid || null;
  } catch {
    // Blocked storage must not blank the landing page - the reference is
    // cosmetic; the balance refresh is the real confirmation.
    return null;
  }
};

/**
 * Extract a displayable rand balance from the AccountDataContext balance
 * object. Mirrors the resolution order used by PrepaidUserDashboard.
 *
 * @param {Object|null} accountBalance
 * @returns {number|null}
 */
export const resolveBalanceRands = (accountBalance) => {
  if (!accountBalance) return null;
  if (typeof accountBalance.balance === 'number') return accountBalance.balance;
  if (typeof accountBalance.remainingCredit === 'number') return accountBalance.remainingCredit;
  return null;
};
